import type { TFunction } from 'i18next';
import colors from 'tailwindcss/colors';

import { t } from '../i18n';

export const SensorTypeCode = {
	SWS: 'SWS',
	RIS: 'RIS',
	NEI: 'NEI',
	TPS: 'TPS',
	EXT: 'EXT',
	MET: 'MET',
	VWS: 'VWS',
	VIB: 'VIB',
	LDI: 'LDI',
	BEU: 'BEU',
	DMS: 'DMS',
} as const;

export type SensorTypeCode = (typeof SensorTypeCode)[keyof typeof SensorTypeCode];

export type AssetSensor = TpsSensorType | MeteoSensorType;

export type TpsSensorType = {
	ID: number;
	typeCode: typeof SensorTypeCode.TPS;
	DataLoggerName: string;
	PointName: string;
	PointCategory: string | null;
	SortIdx: number;
	LastMeasurement: string;
	IsEnabled: 1 | 0;
	TpsID: number;
	MeteoStationID: number;
	LocalX: number;
	LocalY: number;
	LocalZ: number;
	LocationLat: number;
	LocationLon: number;
	LocationHeight: number;
	PrismConst: number;
	AnzEpochen: number;
	WarnLowIsActive: 0 | 1;
	NumWarnLow: number;
	WarnHighIsActive: 0 | 1;
	NumWarnHigh: number;
	AlarmIsActive: 0 | 1;
	NumAlarms: number;
	LastWarnAlarm: string;
	LasUpdate: string;
	SensorName: string;
};

export type MeteoSensorType = {
	ID: number;
	Name: string;
	SerialNumber: string;
	typeCode: typeof SensorTypeCode.MET;
	IsEnabled: 0 | 1;
	AnzEpochen: number;
	LastMeasurement: string;
	MeasIntervalMinutes: number;
	MeasLatencyMinutes: number;
	LocationLat: number;
	LocationLon: number;
	LastUpdate: string;
};

export const SensorStatus = {
	OK: 'ok',
	WARNING_L1: 'warning_l1',
	WARNING_L2: 'warning_l2',
	ALARM: 'alarm',
};
export type SensorStatus = (typeof SensorStatus)[keyof typeof SensorStatus];

export type MappedAssetSensor = {
	id: number;
	uniqId: string;
	label: string;
	dataLoggerName: string;
	dataLoggerId: number;
	name: string;
	type: SensorTypeCode;
	typeSettings: {
		label: string;
	};
	status: SensorStatus;
	statusSettings: {
		badgeStyle: string;
		percentStyle: string;
		label: string;
		map: {
			iconColor: string;
			borderColor: string;
			statusColor: string;
			backgroundColor?: string;
		};
	};
	// Raw timestamp from the API; format it where it is shown, never store a formatted one
	lastReading: string;
	lastWarnAlarm?: string | null;

	numAlarms: number;
	numWarnHigh: number;
	numWarnLow: number;

	coordinates?: {
		lat: number;
		lng: number;
		height: number; // Height is now mandatory (we default to -20)
	};
};

export const getSensorCombinationId = (sensor: Pick<MappedAssetSensor, 'id' | 'type' | 'dataLoggerId'>) => {
	return `${sensor.type}_${sensor.dataLoggerId}_${sensor.id}`;
};

export const statusSettings: Record<SensorStatus, MappedAssetSensor['statusSettings']> = {
	ok: {
		badgeStyle: 'bg-green-100 text-green-700 border-green-200',
		percentStyle: 'text-green-600',
		label: 'OK',
		map: {
			iconColor: colors.green[500],
			borderColor: colors.green[700],
			statusColor: colors.green[600],
			backgroundColor: colors.green[50],
		},
	},
	warning_l1: {
		badgeStyle: 'bg-yellow-100 text-yellow-700 border-yellow-200',
		percentStyle: 'text-yellow-600',
		label: 'Warning L1',
		map: {
			iconColor: colors.yellow[500],
			borderColor: colors.yellow[700],
			statusColor: colors.yellow[600],
			backgroundColor: colors.yellow[50],
		},
	},
	warning_l2: {
		badgeStyle: 'bg-orange-100 text-orange-700 border-orange-200',
		percentStyle: 'text-orange-600',
		label: 'Warning L2',
		map: {
			iconColor: colors.orange[500],
			borderColor: colors.orange[700],
			statusColor: colors.orange[600],
			backgroundColor: colors.orange[50],
		},
	},
	alarm: {
		badgeStyle: 'bg-red-100 text-red-700 border-red-200',
		percentStyle: 'text-red-600',
		label: 'Alarm',
		map: {
			iconColor: colors.red[500],
			borderColor: colors.red[700],
			statusColor: colors.red[600],
			backgroundColor: colors.red[50],
		},
	},
};

export const typeSettings: Record<SensorTypeCode, { label: string }> = {
	[SensorTypeCode.SWS]: { label: 'Strain Wire Sensor' },
	[SensorTypeCode.RIS]: { label: 'Robotic Total Station' },
	[SensorTypeCode.NEI]: { label: 'Nei Sensor' },
	[SensorTypeCode.TPS]: { label: 'Total Station' },
	[SensorTypeCode.EXT]: { label: 'Extensometer' },
	[SensorTypeCode.MET]: { label: 'Meteo Sensor' },
	[SensorTypeCode.VWS]: { label: 'Vibrating Wire Sensor' },
	[SensorTypeCode.VIB]: { label: 'Vibration Sensor' },
	[SensorTypeCode.LDI]: { label: 'Laser Distance Sensor' },
	[SensorTypeCode.BEU]: { label: 'Beu Sensor' },
	[SensorTypeCode.DMS]: { label: 'DMS Sensor' },
};

/**
 * Translated status labels, same keys as `statusSettings`. `mapTpsSensor`/`mapMeteoSensor` bake this into
 * `MappedAssetSensor.statusSettings.label` (badge/map colours are unaffected), so consumers outside this area
 * (loader/SensorChartOverlay.tsx, lib/utils/cesiumSensorStyles.ts) already read translated text through the
 * mapped sensor. Components with their own `t` (e.g. health-monitoring/tabs/sensors.tsx) can still call this
 * directly.
 */
export const sensorStatusLabels = (t: TFunction): Record<SensorStatus, string> => ({
	ok: t('health.status.ok'),
	warning_l1: t('health.status.warningL1'),
	warning_l2: t('health.status.warningL2'),
	alarm: t('health.status.alarm'),
});

/** Translated type labels, same shape as `typeSettings`. See `sensorStatusLabels` for how `mapAssetSensor` uses this. */
export const sensorTypeSettings = (t: TFunction): Record<SensorTypeCode, { label: string }> => ({
	[SensorTypeCode.SWS]: { label: t('health.types.sws') },
	[SensorTypeCode.RIS]: { label: t('health.types.ris') },
	[SensorTypeCode.NEI]: { label: t('health.types.nei') },
	[SensorTypeCode.TPS]: { label: t('health.types.tps') },
	[SensorTypeCode.EXT]: { label: t('health.types.ext') },
	[SensorTypeCode.MET]: { label: t('health.types.met') },
	[SensorTypeCode.VWS]: { label: t('health.types.vws') },
	[SensorTypeCode.VIB]: { label: t('health.types.vib') },
	[SensorTypeCode.LDI]: { label: t('health.types.ldi') },
	[SensorTypeCode.BEU]: { label: t('health.types.beu') },
	[SensorTypeCode.DMS]: { label: t('health.types.dms') },
});

export const isTpsSensor = (sensor: AssetSensor): sensor is TpsSensorType => {
	return sensor.typeCode === SensorTypeCode.TPS;
};

export const isMeteoSensor = (sensor: AssetSensor): sensor is MeteoSensorType => {
	return sensor.typeCode === SensorTypeCode.MET;
};

const getTpsSensorStatus = (sensor: TpsSensorType): SensorStatus => {
	if (sensor.AlarmIsActive > 0) {
		return SensorStatus.ALARM;
	}
	if (sensor.WarnHighIsActive > 0) {
		return SensorStatus.WARNING_L2;
	}
	if (sensor.WarnLowIsActive > 0) {
		return SensorStatus.WARNING_L1;
	}
	return SensorStatus.OK;
};

export function mapTpsSensor(sensor: TpsSensorType): MappedAssetSensor {
	const status = getTpsSensorStatus(sensor);
	const heightVal = sensor.LocationHeight ?? -25;
	return {
		id: sensor.ID,
		uniqId: getSensorCombinationId({
			id: sensor.ID,
			type: SensorTypeCode.TPS,
			dataLoggerId: sensor.TpsID,
		}),
		label: `${SensorTypeCode.TPS}_${sensor.PointName}`,
		dataLoggerName: sensor.DataLoggerName,
		dataLoggerId: sensor.TpsID,
		name: sensor.PointName,
		type: SensorTypeCode.TPS,
		typeSettings: { ...typeSettings[SensorTypeCode.TPS], label: sensorTypeSettings(t)[SensorTypeCode.TPS].label },
		status,
		statusSettings: { ...statusSettings[status], label: sensorStatusLabels(t)[status] },
		lastReading: sensor.LastMeasurement,
		lastWarnAlarm: sensor.LastWarnAlarm,

		numAlarms: sensor.NumAlarms,
		numWarnHigh: sensor.NumWarnHigh,
		numWarnLow: sensor.NumWarnLow,

		coordinates: {
			lat: sensor.LocationLat,
			lng: sensor.LocationLon,
			height: heightVal,
		},
	};
}

export function mapMeteoSensor(sensor: MeteoSensorType): MappedAssetSensor {
	let coordinates: MappedAssetSensor['coordinates'];

	if (sensor.LocationLat && sensor.LocationLon) {
		coordinates = {
			lat: sensor.LocationLat,
			lng: sensor.LocationLon,
			height: 0, // Meteo sensors are usually on the ground/roof
		};
	}

	return {
		id: sensor.ID,
		uniqId: getSensorCombinationId({
			id: sensor.ID,
			type: SensorTypeCode.MET,
			dataLoggerId: sensor.ID,
		}),
		name: sensor.Name,
		dataLoggerName: sensor.Name,
		dataLoggerId: sensor.ID,
		label: `${SensorTypeCode.MET}_${sensor.Name}`,
		type: SensorTypeCode.MET,
		typeSettings: { ...typeSettings[SensorTypeCode.MET], label: sensorTypeSettings(t)[SensorTypeCode.MET].label },
		status: SensorStatus.OK,
		statusSettings: { ...statusSettings[SensorStatus.OK], label: sensorStatusLabels(t)[SensorStatus.OK] },
		lastReading: sensor.LastMeasurement,
		numAlarms: 0,
		numWarnHigh: 0,
		numWarnLow: 0,
		coordinates,
	};
}

export function mapAssetSensor(sensor: AssetSensor): MappedAssetSensor {
	if (isTpsSensor(sensor)) {
		return mapTpsSensor(sensor);
	}
	if (isMeteoSensor(sensor)) {
		return mapMeteoSensor(sensor);
	}
	return {} as MappedAssetSensor;
}
